/**
 * reviews-widget.js
 * Innovazone Reviews Widget — core fetch + render + interactive layer.
 * Phase 3-02: hero badge, 5-band distribution, review list (first 10), empty/error states.
 * Phase 3-03: gallery/lightbox (focus-trap + keyboard + aria), sort, load-more, form interactivity.
 *
 * Brave-safe (WIDGET-06): visible skeleton is in initial DOM; JS only populates data.
 *   Never toggles display on primary sections. Only gallery may be shown/hidden.
 * XSS guard (T-03-04): all review nombre + texto + captions are HTML-escaped via escapeHtml()
 *   before ANY innerHTML insertion. Media URLs are validated before use as src attributes.
 * Media URL guard (T-03-09): validateMediaUrl() rejects javascript:/data: schemes before
 *   any URL is used as img.src or video.src.
 * Form stub (T-03-10): submit stays aria-disabled, zero POST/fetch path in Phase 3.
 */
(function () {
  'use strict';

  /* ─── CONSTANTS ───────────────────────────────────────────────────────────── */
  // Worker endpoint: https://innovazone-reviews.innovazone-cr.workers.dev/reviews
  var WORKER_BASE = 'https://innovazone-reviews.innovazone-cr.workers.dev';

  /**
   * Allowed media URL origins for gallery/lightbox (T-03-09).
   * Only http(s) URLs from trusted CDN/Supabase origins are accepted.
   * javascript: and data: schemes are rejected outright.
   */
  var ALLOWED_MEDIA_PROTOCOLS = /^https?:/i;

  /**
   * 5-band distribution grouping per UI-SPEC § Distribution Bars.
   * Exported on window.RvWidget for 03-03 extension.
   */
  var BANDS = [
    { label: 'Excelente (9-10)', scores: [9, 10] },
    { label: 'Muy bueno (7-8)',  scores: [7, 8]  },
    { label: 'Regular (5-6)',    scores: [5, 6]  },
    { label: 'Bajo (3-4)',       scores: [3, 4]  },
    { label: 'Deficiente (1-2)', scores: [1, 2]  }
  ];

  /* ─── MODULE STATE ────────────────────────────────────────────────────────── */
  /**
   * allReviews: full reviews array from API response.
   * Exposed via window.RvWidget.allReviews so sort/load-more/gallery consume it
   * without a second fetch.
   */
  var allReviews = [];

  /**
   * sortedReviews: the currently displayed ordering (defaults to allReviews order).
   * Managed by the sort wiring; load-more slices from this array.
   */
  var sortedReviews = [];

  /**
   * loadMoreOffset: how many reviews are currently rendered.
   * Starts at 10, increments by 10 each time "Ver más reseñas" is clicked.
   */
  var loadMoreOffset = 10;

  /**
   * lightboxTrigger: element that opened the lightbox — restored focus on close.
   */
  var lightboxTrigger = null;

  /* ─── SECURITY: escapeHtml ────────────────────────────────────────────────── */
  /**
   * Escapes HTML special characters to prevent XSS (T-03-04).
   * Applied to EVERY review nombre + texto + captions before innerHTML insertion.
   * Also applied to file names in error messages.
   *
   * @param {*} str - Any value; coerced to string.
   * @returns {string} HTML-safe string with no raw < > & " '
   */
  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Validates a media URL before using it as img.src / video.src (T-03-09).
   * Rejects javascript:, data:, and any non-http(s) scheme.
   * Returns true if safe, false if rejected.
   *
   * @param {string} url
   * @returns {boolean}
   */
  function validateMediaUrl(url) {
    if (typeof url !== 'string' || !url) return false;
    var trimmed = url.trim();
    return ALLOWED_MEDIA_PROTOCOLS.test(trimmed);
  }

  /**
   * Determines whether a URL is a video by extension.
   * Used to decide between <img> and <video> in gallery/lightbox.
   *
   * @param {string} url
   * @returns {boolean}
   */
  function isVideoUrl(url) {
    return /\.(mp4|webm|mov|ogg)(\?.*)?$/i.test(url);
  }

  /* ─── RENDER: Hero Badge (WIDGET-01) ─────────────────────────────────────── */
  /**
   * Renders the hero badge with average rating and review count.
   * avg_rating === null (zero reviews) → shows "— /10".
   * Removes rv-skeleton from the hero element.
   *
   * @param {Element} root - The #rv-widget section element.
   * @param {{avg_rating: number|null, total_count: number, distribution: Object}} aggregates
   */
  function renderHero(root, aggregates) {
    var hero = root.querySelector('.rv-hero');
    if (!hero) return;

    var avg = aggregates.avg_rating;
    var total = aggregates.total_count || 0;

    var score = (avg !== null && avg !== undefined)
      ? Number(avg).toFixed(1)
      : '—'; // em-dash "—"

    hero.classList.remove('rv-skeleton');
    hero.setAttribute('aria-label', 'Calificación de clientes');
    hero.innerHTML =
      '<div class="rv-hero__left">' +
        '<span class="rv-hero__score">' + escapeHtml(score) + '</span>' +
        '<span class="rv-hero__denom">/10</span>' +
      '</div>' +
      '<div class="rv-hero__right">' +
        '<span class="rv-hero__count"><strong>' + total + '</strong> reseñas</span>' +
      '</div>';
  }

  /**
   * Fills the PDP top mini-badge (under the product title) with the real score.
   * Element: <a data-rv-mini hidden> placed in the product template.
   * Only shown when there is at least one review (Kevin: "solo si tiene reseñas").
   * Clicking it smooth-scrolls down to the full #rv-widget.
   *
   * @param {{avg_rating: number|null, total_count: number}} aggregates
   */
  function renderMiniBadge(aggregates) {
    var el = document.querySelector('[data-rv-mini]');
    if (!el) return;

    var avg = aggregates.avg_rating;
    var total = aggregates.total_count || 0;

    // No reviews → leave hidden (no "—/10" up top).
    if (total <= 0 || avg === null || avg === undefined) {
      el.hidden = true;
      return;
    }

    var score = Number(avg).toFixed(1);
    // 5-star visual mapped from the /10 score (10 → 5 full stars).
    var stars5 = avg / 2;
    var starsHtml = '';
    for (var i = 1; i <= 5; i++) {
      var fillClass = i <= Math.round(stars5) ? 'rv-mini__star--on' : 'rv-mini__star--off';
      starsHtml += '<span class="rv-mini__star ' + fillClass + '">★</span>';
    }

    el.innerHTML =
      '<span class="rv-mini__stars" aria-hidden="true">' + starsHtml + '</span>' +
      '<span class="rv-mini__score">' + escapeHtml(score) + '<span class="rv-mini__denom">/10</span></span>' +
      '<span class="rv-mini__count">(' + total + ' reseña' + (total === 1 ? '' : 's') + ')</span>';
    el.setAttribute('aria-label', 'Calificación ' + score + ' de 10 basada en ' + total + ' reseñas. Ver reseñas.');
    el.hidden = false;

    // Smooth-scroll to the full widget on click (anchor already points at #rv-widget).
    if (!el.dataset.rvMiniWired) {
      el.dataset.rvMiniWired = '1';
      el.addEventListener('click', function (e) {
        var target = document.getElementById('rv-widget');
        if (target) {
          e.preventDefault();
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }
  }

  /* ─── RENDER: Distribution Bars (WIDGET-02) ──────────────────────────────── */
  /**
   * Renders the 5-band distribution bars.
   * Band width = count_in_band / total_count * 100% (0% if total === 0, no div-by-zero).
   * Removes rv-skeleton from the distribution element.
   *
   * @param {Element} root - The #rv-widget section element.
   * @param {{avg_rating: number|null, total_count: number, distribution: Object}} aggregates
   */
  function renderDistribution(root, aggregates) {
    var dist = root.querySelector('.rv-distribution');
    if (!dist) return;

    var distribution = aggregates.distribution || {};
    var total = aggregates.total_count || 0;

    var rowsHtml = BANDS.map(function (band) {
      var count = band.scores.reduce(function (sum, s) {
        return sum + (Number(distribution[String(s)]) || 0);
      }, 0);
      var pct = total > 0 ? Math.round(count / total * 100) : 0;

      return (
        '<div class="rv-dist__row">' +
          '<span class="rv-dist__label">' + escapeHtml(band.label) + '</span>' +
          '<div class="rv-dist__bar-track">' +
            '<div class="rv-dist__bar-fill" style="width:' + pct + '%"></div>' +
          '</div>' +
          '<span class="rv-dist__count">' + count + '</span>' +
        '</div>'
      );
    }).join('');

    dist.classList.remove('rv-skeleton');
    dist.innerHTML =
      '<h3 class="rv-dist__heading">Distribución de notas</h3>' +
      rowsHtml;
  }

  /* ─── RENDER: Single Review Card ─────────────────────────────────────────── */
  /**
   * Builds the HTML for a single review card.
   * ALL user-supplied fields (nombre, texto) are passed through escapeHtml().
   * media_urls thumbnails validated via validateMediaUrl() before use as src.
   *
   * @param {{id, product_id, rating, nombre, texto, media_urls, created_at}} review
   * @param {number} reviewIndex - index in sortedReviews array (for lightbox context)
   * @returns {string} Escaped HTML string for the card.
   */
  function renderCard(review, reviewIndex) {
    var nombre  = escapeHtml(review.nombre  || '');
    var texto   = escapeHtml(review.texto   || '');
    var rating  = parseInt(review.rating, 10) || 0;

    var dateStr = '';
    if (review.created_at) {
      try {
        dateStr = new Date(review.created_at).toLocaleDateString('es-CR', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } catch (e) {
        dateStr = '';
      }
    }

    var mediaUrls = Array.isArray(review.media_urls)
      ? review.media_urls.filter(validateMediaUrl)
      : [];

    var mediaHtml = mediaUrls.slice(0, 3).map(function (url, idx) {
      var safeUrl = escapeHtml(url);
      if (isVideoUrl(url)) {
        // Video thumb: poster placeholder + play icon; data attrs for lightbox
        return (
          '<div' +
            ' class="rv-card__thumb rv-card__thumb--video"' +
            ' tabindex="0"' +
            ' role="button"' +
            ' aria-label="Ver video de reseña"' +
            ' data-review-index="' + escapeHtml(String(reviewIndex)) + '"' +
            ' data-media-index="' + idx + '"' +
          '>' +
            '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" width="24" height="24" aria-hidden="true">' +
              '<path d="M8 5v14l11-7z"/>' +
            '</svg>' +
          '</div>'
        );
      }
      return (
        '<img' +
          ' class="rv-card__thumb"' +
          ' src="' + safeUrl + '"' +
          ' loading="lazy"' +
          ' alt="Foto de reseña de ' + nombre + '"' +
          ' tabindex="0"' +
          ' role="button"' +
          ' aria-label="Ver foto de reseña"' +
          ' data-review-index="' + escapeHtml(String(reviewIndex)) + '"' +
          ' data-media-index="' + idx + '"' +
        '>'
      );
    }).join('');

    return (
      '<article class="rv-review-card">' +
        '<div class="rv-card__score">' + rating + '</div>' +
        '<div class="rv-card__body">' +
          '<span class="rv-card__name">' + nombre + '</span>' +
          '<span class="rv-card__date">' + escapeHtml(dateStr) + '</span>' +
          '<p class="rv-card__text">' + texto + '</p>' +
          (mediaHtml ? '<div class="rv-card__media">' + mediaHtml + '</div>' : '') +
        '</div>' +
      '</article>'
    );
  }

  /* ─── RENDER: Review List (WIDGET-04) ────────────────────────────────────── */
  /**
   * Renders the first 10 review cards into .rv-list__cards.
   * Zero reviews → renders empty-state block per UI-SPEC Copywriting Contract.
   * Resets loadMoreOffset to 10.
   *
   * @param {Element} root - The #rv-widget section element.
   * @param {Array} reviews - The sorted/filtered reviews array to display.
   */
  function renderList(root, reviews) {
    var listCards = root.querySelector('.rv-list__cards');
    var loadMoreWrapper = root.querySelector('.rv-list__more');
    if (!listCards) return;

    // Reset offset when re-rendering from scratch
    loadMoreOffset = 10;

    if (!reviews || reviews.length === 0) {
      listCards.innerHTML =
        '<div class="rv-empty-state">' +
          '<h3 class="rv-empty-state__heading">💬 <strong>Sea el primero en opinar</strong></h3>' +
          '<p class="rv-empty-state__body">' +
            'Este producto aún no tiene reseñas. ' +
            '<strong>Comparta su experiencia</strong> y reciba un 15% de descuento en su próxima compra.' +
          '</p>' +
        '</div>';

      if (loadMoreWrapper) {
        loadMoreWrapper.classList.add('rv-list__more--hidden');
        loadMoreWrapper.setAttribute('aria-hidden', 'true');
      }
      return;
    }

    var firstBatch = reviews.slice(0, 10);
    listCards.innerHTML = firstBatch.map(function (review, idx) {
      return renderCard(review, idx);
    }).join('');

    // Wire card thumb click to lightbox
    wireCardThumbs(root, reviews);

    // Show load-more button if there are more than 10 reviews
    if (loadMoreWrapper) {
      if (reviews.length > 10) {
        loadMoreWrapper.classList.remove('rv-list__more--hidden');
        loadMoreWrapper.removeAttribute('aria-hidden');
      } else {
        loadMoreWrapper.classList.add('rv-list__more--hidden');
        loadMoreWrapper.setAttribute('aria-hidden', 'true');
      }
    }
  }

  /* ─── GALLERY (WIDGET-03) ─────────────────────────────────────────────────── */
  /**
   * Aggregates media_urls across all reviews into a flat list.
   * Each item tracks: { url, nombre, reviewIndex, mediaIndex }
   * Renders 80x80 thumbnails (72x72 mobile via CSS) into .rv-gallery__scroll.
   * Removes display:none + aria-hidden from the gallery only when media exists.
   * Videos get a poster + play icon (full video loads only on lightbox open).
   *
   * @param {Element} root
   * @param {Array} reviews
   */
  function renderGallery(root, reviews) {
    var gallery = root.querySelector('.rv-gallery');
    var scrollEl = root.querySelector('.rv-gallery__scroll');
    if (!gallery || !scrollEl) return;

    // Aggregate all media items with context
    var mediaItems = [];
    reviews.forEach(function (review, rIdx) {
      var urls = Array.isArray(review.media_urls)
        ? review.media_urls.filter(validateMediaUrl)
        : [];
      urls.forEach(function (url, mIdx) {
        mediaItems.push({
          url: url,
          nombre: review.nombre || '',
          reviewIndex: rIdx,
          mediaIndex: mIdx,
          globalIndex: mediaItems.length  // will be set after push; corrected below
        });
      });
    });

    // Correct globalIndex after the fact
    mediaItems.forEach(function (item, i) {
      item.globalIndex = i;
    });

    if (mediaItems.length === 0) {
      // Keep gallery hidden — 0 media items (the one allowed display:none)
      gallery.style.display = 'none';
      gallery.setAttribute('aria-hidden', 'true');
      return;
    }

    // Render thumbnails
    var thumbsHtml = mediaItems.map(function (item) {
      var safeUrl = escapeHtml(item.url);
      var safeName = escapeHtml(item.nombre);

      if (isVideoUrl(item.url)) {
        return (
          '<div' +
            ' class="rv-gallery__item rv-gallery__video-thumb"' +
            ' tabindex="0"' +
            ' role="listitem button"' +
            ' aria-label="Ver video de reseña de ' + safeName + '"' +
            ' data-gallery-index="' + item.globalIndex + '"' +
          '>' +
            '<div class="rv-gallery__play-icon" aria-hidden="true">' +
              '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" width="28" height="28">' +
                '<path d="M8 5v14l11-7z"/>' +
              '</svg>' +
            '</div>' +
          '</div>'
        );
      }

      return (
        '<div' +
          ' class="rv-gallery__item"' +
          ' tabindex="0"' +
          ' role="listitem button"' +
          ' aria-label="Ver foto de reseña de ' + safeName + '"' +
          ' data-gallery-index="' + item.globalIndex + '"' +
        '>' +
          '<img' +
            ' src="' + safeUrl + '"' +
            ' loading="lazy"' +
            ' alt="Foto de reseña de ' + safeName + '"' +
            ' width="80"' +
            ' height="80"' +
          '>' +
        '</div>'
      );
    }).join('');

    scrollEl.innerHTML = thumbsHtml;

    // Show the gallery section
    gallery.style.display = '';
    gallery.removeAttribute('aria-hidden');

    // Wire gallery item clicks to lightbox
    scrollEl.addEventListener('click', function (e) {
      var item = e.target.closest('[data-gallery-index]');
      if (!item) return;
      var idx = parseInt(item.getAttribute('data-gallery-index'), 10);
      openLightbox(mediaItems, idx, item);
    });

    scrollEl.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var item = e.target.closest('[data-gallery-index]');
      if (!item) return;
      e.preventDefault();
      var idx = parseInt(item.getAttribute('data-gallery-index'), 10);
      openLightbox(mediaItems, idx, item);
    });
  }

  /* ─── LIGHTBOX (WIDGET-03 interaction) ───────────────────────────────────── */
  /**
   * Opens a full-screen lightbox overlay with prev/next navigation.
   * Items is an array of { url, nombre } objects.
   * Focus is trapped inside the overlay while open; restored to trigger on close.
   * Videos: <video controls playsinline> — never autoplay audio.
   * Keyboard: Escape closes, ArrowLeft/Right navigates.
   *
   * @param {Array} items - Array of { url, nombre } media items.
   * @param {number} startIndex - Index in items to show first.
   * @param {Element} [trigger] - The element that triggered the open (for focus restore).
   */
  function openLightbox(items, startIndex, trigger) {
    if (!items || items.length === 0) return;

    lightboxTrigger = trigger || null;
    var currentIndex = Math.max(0, Math.min(startIndex, items.length - 1));

    // Create overlay
    var overlay = document.createElement('div');
    overlay.className = 'rv-lightbox';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Visor de medios');

    // Close button
    var closeBtn = document.createElement('button');
    closeBtn.className = 'rv-lightbox__close';
    closeBtn.setAttribute('type', 'button');
    closeBtn.setAttribute('aria-label', 'Cerrar');
    closeBtn.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" width="20" height="20" aria-hidden="true">' +
        '<line x1="18" y1="6" x2="6" y2="18"></line>' +
        '<line x1="6" y1="6" x2="18" y2="18"></line>' +
      '</svg>';

    // Prev button
    var prevBtn = document.createElement('button');
    prevBtn.className = 'rv-lightbox__nav rv-lightbox__prev';
    prevBtn.setAttribute('type', 'button');
    prevBtn.setAttribute('aria-label', 'Imagen anterior');
    prevBtn.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" width="20" height="20" aria-hidden="true">' +
        '<polyline points="15 18 9 12 15 6"></polyline>' +
      '</svg>';

    // Next button
    var nextBtn = document.createElement('button');
    nextBtn.className = 'rv-lightbox__nav rv-lightbox__next';
    nextBtn.setAttribute('type', 'button');
    nextBtn.setAttribute('aria-label', 'Imagen siguiente');
    nextBtn.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" width="20" height="20" aria-hidden="true">' +
        '<polyline points="9 18 15 12 9 6"></polyline>' +
      '</svg>';

    // Media container
    var mediaContainer = document.createElement('div');
    mediaContainer.className = 'rv-lightbox__content';

    overlay.appendChild(closeBtn);
    overlay.appendChild(prevBtn);
    overlay.appendChild(mediaContainer);
    overlay.appendChild(nextBtn);
    document.body.appendChild(overlay);

    // Render current media item
    function renderMedia(index) {
      currentIndex = Math.max(0, Math.min(index, items.length - 1));
      var item = items[currentIndex];
      mediaContainer.innerHTML = '';

      if (isVideoUrl(item.url)) {
        var video = document.createElement('video');
        video.className = 'rv-lightbox__video';
        video.setAttribute('controls', '');
        video.setAttribute('playsinline', '');
        // NEVER autoplay audio (UI-SPEC requirement)
        video.setAttribute('muted', '');
        video.setAttribute('preload', 'metadata');
        video.setAttribute('aria-label', 'Video de reseña de ' + escapeHtml(item.nombre || ''));
        video.src = item.url; // URL validated by validateMediaUrl before entering items array
        mediaContainer.appendChild(video);
      } else {
        var img = document.createElement('img');
        img.className = 'rv-lightbox__img';
        img.src = item.url; // URL validated by validateMediaUrl
        img.alt = 'Foto de reseña de ' + escapeHtml(item.nombre || '');
        mediaContainer.appendChild(img);
      }

      // Update nav button visibility
      prevBtn.style.display = items.length <= 1 ? 'none' : '';
      nextBtn.style.display = items.length <= 1 ? 'none' : '';
    }

    renderMedia(currentIndex);

    // Close handler
    function closeLightbox() {
      document.removeEventListener('keydown', keyHandler);
      if (overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
      // Restore focus to trigger element
      if (lightboxTrigger && typeof lightboxTrigger.focus === 'function') {
        lightboxTrigger.focus();
      }
      lightboxTrigger = null;
    }

    // Focus trap: cycle Tab within overlay
    function getFocusable() {
      return Array.prototype.slice.call(
        overlay.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter(function (el) {
        return !el.disabled && el.offsetParent !== null;
      });
    }

    function keyHandler(e) {
      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          closeLightbox();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          renderMedia(currentIndex - 1);
          break;
        case 'ArrowRight':
          e.preventDefault();
          renderMedia(currentIndex + 1);
          break;
        case 'Tab':
          var focusable = getFocusable();
          if (!focusable.length) { e.preventDefault(); break; }
          var first = focusable[0];
          var last = focusable[focusable.length - 1];
          if (e.shiftKey) {
            if (document.activeElement === first) {
              e.preventDefault();
              last.focus();
            }
          } else {
            if (document.activeElement === last) {
              e.preventDefault();
              first.focus();
            }
          }
          break;
      }
    }

    document.addEventListener('keydown', keyHandler);

    // Button click handlers
    closeBtn.addEventListener('click', closeLightbox);
    prevBtn.addEventListener('click', function () { renderMedia(currentIndex - 1); });
    nextBtn.addEventListener('click', function () { renderMedia(currentIndex + 1); });

    // Close on backdrop click (not on content)
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) {
        closeLightbox();
      }
    });

    // Focus the close button on open (focus-trap anchor)
    closeBtn.focus();
  }

  /* ─── SORT CONTROLS (WIDGET-04) ──────────────────────────────────────────── */
  /**
   * Wires the sort radiogroup buttons.
   * Clicking a sort button re-sorts allReviews and calls renderList.
   * Active button gets aria-checked="true" + rv-sort__btn--active class.
   *
   * Sort modes per UI-SPEC:
   *   recent   — created_at desc (default)
   *   highest  — rating desc
   *   lowest   — rating asc
   *   media    — reviews with media_urls.length > 0 first
   *
   * @param {Element} root
   */
  function wireSortControls(root) {
    var sortGroup = root.querySelector('.rv-sort');
    if (!sortGroup) return;

    var buttons = sortGroup.querySelectorAll('[data-sort]');
    if (!buttons.length) return;

    function applySortMode(mode) {
      var reviews = allReviews.slice(); // shallow copy to avoid mutating source

      switch (mode) {
        case 'highest':
          reviews.sort(function (a, b) {
            return (Number(b.rating) || 0) - (Number(a.rating) || 0);
          });
          break;
        case 'lowest':
          reviews.sort(function (a, b) {
            return (Number(a.rating) || 0) - (Number(b.rating) || 0);
          });
          break;
        case 'media':
          reviews.sort(function (a, b) {
            var aHas = Array.isArray(a.media_urls) && a.media_urls.length > 0 ? 1 : 0;
            var bHas = Array.isArray(b.media_urls) && b.media_urls.length > 0 ? 1 : 0;
            if (bHas !== aHas) return bHas - aHas;
            // Secondary sort: recent within same group
            return new Date(b.created_at || 0) - new Date(a.created_at || 0);
          });
          break;
        case 'recent':
        default:
          reviews.sort(function (a, b) {
            return new Date(b.created_at || 0) - new Date(a.created_at || 0);
          });
          break;
      }

      sortedReviews = reviews;
      renderList(root, sortedReviews);

      // Re-wire card thumbs after re-render
      wireCardThumbs(root, sortedReviews);

      // Re-wire load-more with new sorted array
      wireLoadMore(root);
    }

    sortGroup.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-sort]');
      if (!btn) return;

      var mode = btn.getAttribute('data-sort');

      // Update aria-checked + active class
      buttons.forEach(function (b) {
        b.setAttribute('aria-checked', 'false');
        b.classList.remove('rv-sort__btn--active');
      });
      btn.setAttribute('aria-checked', 'true');
      btn.classList.add('rv-sort__btn--active');

      applySortMode(mode);
    });
  }

  /* ─── LOAD-MORE (WIDGET-04) ──────────────────────────────────────────────── */
  /**
   * Wires the "Ver más reseñas" button.
   * Appends the next 10 reviews from sortedReviews (no re-fetch).
   * Hides itself when all reviews are displayed.
   *
   * @param {Element} root
   */
  function wireLoadMore(root) {
    var wrapper = root.querySelector('.rv-list__more');
    var btn = wrapper ? wrapper.querySelector('button') : null;
    if (!btn) return;

    // Remove any prior listener by replacing the button clone
    var newBtn = btn.cloneNode(true);
    btn.parentNode.replaceChild(newBtn, btn);

    newBtn.addEventListener('click', function () {
      var listCards = root.querySelector('.rv-list__cards');
      if (!listCards) return;

      var nextBatch = sortedReviews.slice(loadMoreOffset, loadMoreOffset + 10);
      if (!nextBatch.length) return;

      var fragment = document.createDocumentFragment();
      nextBatch.forEach(function (review, i) {
        var div = document.createElement('div');
        div.innerHTML = renderCard(review, loadMoreOffset + i);
        while (div.firstChild) {
          fragment.appendChild(div.firstChild);
        }
      });

      listCards.appendChild(fragment);
      loadMoreOffset += nextBatch.length;

      // Wire newly-added card thumbs
      wireCardThumbs(root, sortedReviews);

      // Hide load-more if all rendered
      if (loadMoreOffset >= sortedReviews.length) {
        wrapper.classList.add('rv-list__more--hidden');
        wrapper.setAttribute('aria-hidden', 'true');
      }
    });
  }

  /* ─── CARD THUMB → LIGHTBOX WIRING ─────────────────────────────────────── */
  /**
   * Wires click + keydown on card media thumbnails to openLightbox.
   * Called after renderList + after each load-more append.
   * Uses delegated events on .rv-list__cards to handle newly appended cards.
   *
   * @param {Element} root
   * @param {Array} reviews - The current sorted reviews array.
   */
  function wireCardThumbs(root, reviews) {
    var listCards = root.querySelector('.rv-list__cards');
    if (!listCards) return;

    // Remove old listener by replacing the container's listener via a flag
    // We use a named delegated handler stored on the element to avoid duplicates
    if (listCards._rvThumbHandler) {
      listCards.removeEventListener('click', listCards._rvThumbHandler);
      listCards.removeEventListener('keydown', listCards._rvThumbKeyHandler);
    }

    function handleThumbActivate(e, targetEl) {
      var thumb = targetEl.closest('[data-review-index][data-media-index]');
      if (!thumb) return;

      var rIdx = parseInt(thumb.getAttribute('data-review-index'), 10);
      var mIdx = parseInt(thumb.getAttribute('data-media-index'), 10);

      if (isNaN(rIdx) || isNaN(mIdx)) return;

      var review = reviews[rIdx];
      if (!review) return;

      var urls = Array.isArray(review.media_urls)
        ? review.media_urls.filter(validateMediaUrl)
        : [];

      // Build items for this review's media
      var items = urls.map(function (url) {
        return { url: url, nombre: review.nombre || '' };
      });

      if (!items.length) return;

      var safeIdx = Math.min(mIdx, items.length - 1);
      openLightbox(items, safeIdx, thumb);
    }

    listCards._rvThumbHandler = function (e) {
      handleThumbActivate(e, e.target);
    };

    listCards._rvThumbKeyHandler = function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var thumb = e.target.closest('[data-review-index][data-media-index]');
      if (!thumb) return;
      e.preventDefault();
      handleThumbActivate(e, e.target);
    };

    listCards.addEventListener('click', listCards._rvThumbHandler);
    listCards.addEventListener('keydown', listCards._rvThumbKeyHandler);
  }

  /* ─── FORM INTERACTIVITY (WIDGET-05) ─────────────────────────────────────── */
  /**
   * Wires:
   *  1. Rating selector 1-10 buttons: single-select toggle via aria-pressed.
   *  2. File inputs: client-side pre-check (count + size) — UX only, NOT security gate.
   *  3. Submit button: stays aria-disabled, shows stub message on click. NO POST path.
   *
   * All copy verbatim from UI-SPEC § Copywriting Contract.
   *
   * @param {Element} root
   */
  function wireFormInteractivity(root) {
    var form = root.querySelector('#rv-review-form');
    if (!form) return;

    // ── 1. Rating selector ──────────────────────────────────────────────────
    var ratingBtns = form.querySelectorAll('.rv-rating-btn');
    ratingBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        // Deselect all, select clicked
        ratingBtns.forEach(function (b) {
          b.setAttribute('aria-pressed', 'false');
        });
        btn.setAttribute('aria-pressed', 'true');
      });
    });

    // ── 2. File input pre-checks (client-side UX only — NOT the security gate) ──
    var fotosInput = form.querySelector('#rv-fotos');
    var videosInput = form.querySelector('#rv-videos');
    var fotosError = form.querySelector('.rv-form__error--fotos');
    var videosError = form.querySelector('.rv-form__error--videos');

    if (fotosInput && fotosError) {
      fotosInput.addEventListener('change', function () {
        var files = Array.prototype.slice.call(fotosInput.files || []);
        var MAX_SIZE = 5 * 1024 * 1024; // 5 MB
        var MAX_COUNT = 5;
        var errors = [];

        if (files.length > MAX_COUNT) {
          errors.push('Máximo ' + MAX_COUNT + ' fotos permitidas');
        }

        files.forEach(function (file) {
          if (file.size > MAX_SIZE) {
            errors.push('El archivo "' + escapeHtml(file.name) + '" supera el tamaño permitido');
          }
        });

        fotosError.textContent = errors.join(' · ');
      });
    }

    if (videosInput && videosError) {
      videosInput.addEventListener('change', function () {
        var files = Array.prototype.slice.call(videosInput.files || []);
        var MAX_SIZE = 50 * 1024 * 1024; // 50 MB
        var MAX_COUNT = 2;
        var errors = [];

        if (files.length > MAX_COUNT) {
          errors.push('Máximo ' + MAX_COUNT + ' videos permitidos');
        }

        files.forEach(function (file) {
          if (file.size > MAX_SIZE) {
            errors.push('El archivo "' + escapeHtml(file.name) + '" supera el tamaño permitido');
          }
        });

        videosError.textContent = errors.join(' · ');
      });
    }

    // ── 3. Submit — POST real al Worker: sube media, crea reseña, revela código ──
    var submitBtn = form.querySelector('.rv-btn--submit');
    var statusMsg = form.querySelector('.rv-form__status');
    var productId = Number(root.getAttribute('data-product-id') || '0');
    var sending = false;

    function setStatus(text, kind) {
      if (!statusMsg) return;
      statusMsg.textContent = text || '';
      statusMsg.className = 'rv-form__status' + (kind ? ' rv-form__status--' + kind : '');
    }

    function uploadOne(file) {
      var fd = new FormData();
      fd.append('file', file);
      return fetch(WORKER_BASE + '/upload', { method: 'POST', body: fd }).then(function (res) {
        return res.json().then(function (j) {
          if (!res.ok || !j.url) throw new Error(j.error || 'No se pudo subir el archivo.');
          return j.url;
        });
      });
    }

    function renderSuccess(code, message) {
      var wrapper = root.querySelector('.rv-form-wrapper');
      if (!wrapper) return;
      var body = code
        ? '<p class="rv-form__sub">Su código de <strong>15% de descuento</strong> para su próxima compra:</p>' +
          '<p class="rv-form__code"><strong>' + escapeHtml(code) + '</strong></p>' +
          '<p class="rv-form__sub">Tómele una foto a este código y envíelo por WhatsApp para aplicarlo.</p>'
        : '<p class="rv-form__sub">' + escapeHtml(message || 'Su reseña fue registrada. Su código de descuento llegará en las próximas horas.') + '</p>';
      wrapper.innerHTML =
        '<div class="rv-form__success" role="status" aria-live="polite">' +
          '<h2 class="rv-section__heading">🎉 <strong>¡Gracias por su reseña!</strong></h2>' +
          body +
        '</div>';
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (sending) return;

      var pressed = form.querySelector('.rv-rating-btn[aria-pressed="true"]');
      var rating = pressed ? Number(pressed.getAttribute('data-rating')) : 0;
      var nombreInput = form.querySelector('#rv-nombre');
      var textoInput = form.querySelector('#rv-texto');
      var nombre = (nombreInput && nombreInput.value ? nombreInput.value : '').trim();
      var texto = (textoInput && textoInput.value ? textoInput.value : '').trim();

      var ratingError = form.querySelector('.rv-form__error--rating');
      var nombreError = form.querySelector('.rv-form__error--nombre');
      var textoError = form.querySelector('.rv-form__error--texto');
      if (ratingError) ratingError.textContent = '';
      if (nombreError) nombreError.textContent = '';
      if (textoError) textoError.textContent = '';

      var bad = false;
      if (!rating) {
        if (ratingError) ratingError.textContent = 'Escoja una nota del 1 al 10.';
        bad = true;
      }
      if (!nombre) {
        if (nombreError) nombreError.textContent = 'Escriba su nombre.';
        bad = true;
      }
      if (texto.length < 20) {
        if (textoError) textoError.textContent = 'Su reseña debe tener al menos 20 caracteres.';
        bad = true;
      }
      if (!productId) {
        setStatus('No se pudo identificar el producto. Recargue la página.', 'error');
        return;
      }
      if (bad) {
        setStatus('Revise los campos marcados.', 'error');
        return;
      }

      var files = []
        .concat(Array.prototype.slice.call((form.querySelector('#rv-fotos') || {}).files || []))
        .concat(Array.prototype.slice.call((form.querySelector('#rv-videos') || {}).files || []));

      sending = true;
      if (submitBtn) submitBtn.setAttribute('aria-disabled', 'true');
      setStatus(files.length ? 'Subiendo sus archivos…' : 'Enviando su reseña…', 'busy');

      var chain = Promise.resolve([]);
      files.forEach(function (file) {
        chain = chain.then(function (urls) {
          return uploadOne(file).then(function (url) {
            return urls.concat([url]);
          });
        });
      });

      chain
        .then(function (mediaUrls) {
          setStatus('Enviando su reseña…', 'busy');
          return fetch(WORKER_BASE + '/review', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              product_id: productId,
              rating: rating,
              nombre: nombre,
              texto: texto,
              media_urls: mediaUrls
            })
          });
        })
        .then(function (res) {
          return res.json().then(function (j) {
            if (!res.ok || !j.ok) throw new Error(j.error || j.message || 'No se pudo enviar su reseña.');
            return j;
          });
        })
        .then(function (j) {
          renderSuccess(j.discount_code, j.message);
        })
        .catch(function (err) {
          sending = false;
          if (submitBtn) submitBtn.removeAttribute('aria-disabled');
          setStatus(err && err.message ? err.message : 'No se pudo enviar su reseña. Intente de nuevo.', 'error');
        });
    });

  }

  /* ─── FETCH + ORCHESTRATE ────────────────────────────────────────────────── */
  /**
   * Main entry point — called on DOMContentLoaded.
   * Reads data-product-id from #rv-widget, fetches Worker, orchestrates renders.
   * On fetch failure: logs warning, leaves skeleton visible (Brave-safe degrade).
   * On success: stores allReviews on module state + window.RvWidget for external consumers.
   */
  function init() {
    var root = document.getElementById('rv-widget');
    if (!root) return;

    var productId = root.dataset.productId;
    if (!productId) return;

    // Wire form interactivity immediately (does not depend on fetch)
    wireFormInteractivity(root);

    var url = WORKER_BASE + '/reviews?product_id=' + encodeURIComponent(productId);

    fetch(url)
      .then(function (res) {
        if (!res.ok) {
          throw new Error('HTTP ' + res.status);
        }
        return res.json();
      })
      .then(function (data) {
        var reviews    = Array.isArray(data.reviews) ? data.reviews : [];
        var aggregates = data.aggregates || { avg_rating: null, total_count: 0, distribution: {} };

        // Store full array for sort/load-more/gallery — copy so sort doesn't mutate
        allReviews = reviews.slice();
        sortedReviews = reviews.slice();

        if (window.RvWidget) {
          window.RvWidget.allReviews = allReviews;
        }

        renderHero(root, aggregates);
        renderMiniBadge(aggregates);
        renderDistribution(root, aggregates);
        renderList(root, sortedReviews);
        renderGallery(root, reviews);

        // Wire sort controls + load-more after data arrives
        wireSortControls(root);
        wireLoadMore(root);

        // Notify external extension hook (if registered)
        if (typeof window.RvWidget === 'object' && typeof window.RvWidget.onDataReady === 'function') {
          window.RvWidget.onDataReady(root, reviews, aggregates);
        }
      })
      .catch(function (err) {
        // Brave-safe degrade: leave visible skeleton, do NOT blank the page or throw.
        console.warn('[rv-widget] fetch failed — skeleton remains visible:', err);
      });
  }

  /* ─── PUBLIC MODULE SURFACE (extensible by downstream plans) ───────────── */
  /**
   * window.RvWidget exposes pure helpers and state so downstream plans can:
   *  - Access allReviews (sort/load-more without a second fetch)
   *  - Reuse BANDS (for any distribution re-render)
   *  - Reuse escapeHtml / renderCard / validateMediaUrl / openLightbox
   *  - Register onDataReady callback before DOMContentLoaded fires
   */
  window.RvWidget = {
    /* Immutable helpers */
    escapeHtml: escapeHtml,
    validateMediaUrl: validateMediaUrl,
    renderCard: renderCard,
    BANDS: BANDS,
    WORKER_BASE: WORKER_BASE,

    /* Interactive functions */
    openLightbox: openLightbox,
    renderGallery: renderGallery,

    /* Render functions (root, data) — no live DOM coupling beyond passed root */
    renderHero: renderHero,
    renderDistribution: renderDistribution,
    renderList: renderList,

    /* Module state — set after fetch resolves */
    allReviews: allReviews,

    /**
     * Extension hook: set this before DOMContentLoaded fires to be
     * called once data is ready. Signature: function(root, reviews, aggregates)
     */
    onDataReady: null
  };

  /* ─── BOOT ────────────────────────────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // DOMContentLoaded already fired (e.g. script loaded async after parse)
    init();
  }

})();
